// src/web/ServicesPage.jsx

import { useState } from 'react';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import AVIcon from '../../components/icons/AllVoicedIcons';
import ContactFormModal from '../../components/modals/web-modals/ContactFormModal';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';
import { serviceRoutesById } from '../../navigation/publicServiceRoutes';

const ServicesPage = () => {
  const { t } = useTranslation();

  const [isContactFormModalOpen, setContactFormModalOpen] = useState(false);

  const openContactFormModal = () => setContactFormModalOpen(true);
  const closeContactFormModal = () => setContactFormModalOpen(false);

  const serviceCards = [
    {
      id: 'perceptual-skill-development',
      icon: 'listen',
      title: serviceRoutesById['perceptual-skill-development'].label,
      proposition: t(
        'services_skill_development_overview',
        'Develop auditory-perceptual voice evaluation skills through structured listening, comparison, feedback, and facilitated group experiences.'
      ),
      capabilities: [
        t('services_skill_development_capability_1', 'Independent listening activities'),
        t('services_skill_development_capability_2', 'Feedback and perceptual references where available'),
        t('services_skill_development_capability_3', 'Live Sessions and group learning'),
      ],
      cta: t('services_skill_development_cta', 'Explore skill development'),
      path: serviceRoutesById['perceptual-skill-development'].path,
    },
    {
      id: 'institutional-programs',
      icon: 'graduationCap',
      title: serviceRoutesById['institutional-programs'].label,
      proposition: t(
        'services_institutional_programs_overview',
        'Configure structured educational implementation for courses, cohorts, assessment, learning analytics, and educational research.'
      ),
      capabilities: [
        t('services_institutional_programs_capability_1', 'Course and module integration'),
        t('services_institutional_programs_capability_2', 'Cohort and program workflows'),
        t('services_institutional_programs_capability_3', 'Competency evidence and educational research'),
      ],
      cta: t('services_institutional_programs_cta', 'Explore institutional programs'),
      path: serviceRoutesById['institutional-programs'].path,
    },
    {
      id: 'perceptual-research-infrastructure',
      // TODO: The canonical service identity icon for Research Infrastructure
      // remains intentionally undecided pending future architectural positioning.
      icon: 'research',
      title: serviceRoutesById['perceptual-research-infrastructure'].label,
      proposition: t(
        'services_research_infrastructure_overview',
        'Design and coordinate structured perceptual workflows for clinical and research studies.'
      ),
      capabilities: [
        t('services_research_infrastructure_capability_1', 'Perceptual protocol configuration'),
        t('services_research_infrastructure_capability_2', 'Evaluator and sample workflows'),
        t('services_research_infrastructure_capability_3', 'Structured research data and exports'),
      ],
      cta: t('services_research_infrastructure_cta', 'Explore research infrastructure'),
      path: serviceRoutesById['perceptual-research-infrastructure'].path,
    },
    {
      id: 'perceptual-data-annotation',
      icon: 'tags',
      title: serviceRoutesById['perceptual-data-annotation'].label,
      proposition: t(
        'services_data_annotation_overview',
        'Generate structured human-perceptual data for voice datasets through defined protocols, evaluator workflows, and multirater annotation.'
      ),
      capabilities: [
        t('services_data_annotation_capability_1', 'Project-specific annotation protocols'),
        t('services_data_annotation_capability_2', 'Agreement, variability, and uncertainty-aware outputs'),
        t('services_data_annotation_capability_3', 'Research, benchmarking, and technology evaluation'),
      ],
      cta: t('services_data_annotation_cta', 'Explore data annotation'),
      path: serviceRoutesById['perceptual-data-annotation'].path,
    },
  ];

  const comparisonCards = [
    {
      title: t('services_compare_people_title', 'Develop people'),
      text: serviceRoutesById['perceptual-skill-development'].label,
    },
    {
      title: t('services_compare_education_title', 'Implement education'),
      text: serviceRoutesById['institutional-programs'].label,
    },
    {
      title: t('services_compare_studies_title', 'Run studies'),
      text: serviceRoutesById['perceptual-research-infrastructure'].label,
    },
    {
      title: t('services_compare_datasets_title', 'Build datasets'),
      text: serviceRoutesById['perceptual-data-annotation'].label,
    },
  ];

  return (
    <div className="services-page">
      <PageTracker pageName="ServicesPage" />

      {/* HERO */}
      <section className="services-hero">
        <span className="services-eyebrow">
          {t('services_eyebrow', 'All-Voiced Services')}
        </span>

        <h1>
          {t(
            'services_title',
            'Auditory-perceptual voice evaluation services'
          )}
        </h1>

        <p>
        {t(
            'services_intro',
            'All-Voiced supports individuals, educators, institutions, researchers, dataset owners, and voice technology teams through connected services for perceptual skill development, institutional implementation, research workflows, and structured perceptual data generation.'
        )}
        </p>

        <div className="services-hero-cta web-cta-row">
          <button
            type="button"
            onClick={openContactFormModal}
            className="web-button web-button--secondary"
          >
            {t('services_cta', 'Explore a collaboration')}
          </button>
          <Link to="/meet" className="web-button web-button--primary">
            Schedule a meeting
          </Link>
        </div>

        <span className="services-hero-note">
        {t(
            'services_cta_note',
            'Built for voice science, education, clinical training, and voice technology.'
        )}
        </span>
      </section>

      {/* SERVICE LINES */}
      <section className="services-section">
        <h2>{t('services_lines_title', 'Service lines')}</h2>

        <p>
          {t(
            'services_lines_intro',
            'Use this overview to identify the All-Voiced service line that best matches your current context.'
          )}
        </p>

        <div className="services-grid">
          {serviceCards.map((service) => (
            <div className="services-card" key={service.id}>
              <div className="services-card-icon">
                <AVIcon icon={service.icon} size={28} color="#209385" />
              </div>

              <h3>{service.title}</h3>
              <p className="services-micro">{service.proposition}</p>

              <ul>
                {service.capabilities.map((capability) => (
                  <li key={capability}>{capability}</li>
                ))}
              </ul>

              <Link
                to={service.path}
                className="web-button web-button--secondary services-card-button"
              >
                {service.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="services-section services-highlight">
        <span className="services-label">
          {t('services_access_pricing_label', 'Access & pricing')}
        </span>

        <h2>
          {t('services_access_pricing_title', 'Not sure how access and pricing work?')}
        </h2>

        <p>
          {t(
            'services_access_pricing_text',
            'Review how free and paid catalog entries, institutional implementations, research workflows, and annotation projects are accessed and scoped.'
          )}
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            to={serviceRoutesById['access-and-pricing'].path}
            className="web-button web-button--secondary"
          >
            {t('services_access_pricing_cta', 'Explore Access & Pricing')}
          </Link>
        </div>
      </section>

      <section className="services-section services-highlight">
        <span className="services-label">
          {t('services_comparison_label', 'Service orientation')}
        </span>

        <h2>
          {t('services_comparison_title', 'Different entry points, connected infrastructure')}
        </h2>

        <p>
          {t(
            'services_comparison_intro',
            'All four service lines are connected by auditory-perceptual voice evaluation, but each one starts from a different user need.'
          )}
        </p>

        <div className="services-grid">
          {comparisonCards.map((item) => (
            <div className="services-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW WE WORK */}
    <section className="services-section">
    <h2>{t('services_process_title', 'How we work')}</h2>

    <div className="services-steps">
        <div className="services-step">
        <span className="services-step-number">1</span>
        <div>
            <h3>{t('services_step1_title', 'Understand your context')}</h3>
            <p>
            {t(
                'services_step1_text',
                'We clarify your goals, audience, educational context, study, dataset, timeline, and constraints.'
            )}
            </p>
        </div>
        </div>

        <div className="services-step">
        <span className="services-step-number">2</span>
        <div>
        <h3>{t('services_step2_title', 'Define the implementation scope')}</h3>
            <p>
            {t(
                'services_step2_text',
                'We identify the most appropriate starting point — from self-directed activities to institutional programs, research workflows, or dataset annotation.'
            )}
            </p>
        </div>
        </div>

        <div className="services-step">
        <span className="services-step-number">3</span>
        <div>
            <h3>{t('services_step3_title', 'Design and configure the workflow')}</h3>
            <p>
            {t(
                'services_step3_text',
                'We configure the relevant activities, protocols, evaluator workflows, feedback, reporting, or data outputs according to the agreed scope.'
            )}
            </p>
        </div>
        </div>

        <div className="services-step">
        <span className="services-step-number">4</span>
        <div>
            <h3>{t('services_step4_title', 'Deliver and scale')}</h3>
            <p>
            {t(
                'services_step4_text',
                'You receive the agreed outputs and can decide whether to continue, expand, or move into a more configured implementation.'
            )}
            </p>
        </div>
        </div>
    </div>
    </section>

      {/* FINAL CTA */}
      <section className="services-final-cta">
        <h2>
          {t(
            'services_final_title',
            'Not sure which service line fits your context?'
          )}
        </h2>

        <p>
          {t(
            'services_final_text',
            'Share your goals, study, dataset, educational context, or training needs and identify the most appropriate starting point.'
          )}
        </p>

        <div className="services-hero-cta web-cta-row">
        <Link to="/meet" className="web-button web-button--primary web-button--on-dark">
          Schedule a meeting
        </Link>

          <button
            type="button"
            onClick={openContactFormModal}
            className="web-button web-button--secondary web-button--on-dark"
          >
            {t('services_final_contact', 'Get in touch')}
          </button>
        </div>
      </section>

      <ContactFormModal
        isOpen={isContactFormModalOpen}
        onRequestClose={closeContactFormModal}
      />

      <ScrollTracker pageName="ServicesPage" />
    </div>
  );
};

export default ServicesPage;
